import * as Sentry from "@sentry/react";
import axios, { AxiosError } from "axios";
import { storage, STORAGE_KEYS } from "./Storage";

export default class Api {
  private http = axios.create({
    baseURL: process.env.REACT_APP_ApiUrl,
    timeout: 60000, // 60 seconds timeout
    headers: {
      module: "Common",
    },
  });

  constructor() {
    // Add request interceptor for better error handling
    this.http.interceptors.response.use(
      (response) => response,
      (rawError: unknown) => {
        if (axios.isCancel(rawError)) {
          throw rawError;
        }
        const error = rawError as AxiosError;
        if (error.name === "CanceledError") {
          throw error;
        }

        // Log to Sentry
        Sentry.captureException(error);

        // More specific error messages
        if (error.code === "NETWORK_ERROR" || !error.response) {
          console.error("Network Error: Unable to connect to API at", process.env.REACT_APP_ApiUrl);
          throw new Error(`Network Error: Unable to connect to ${process.env.REACT_APP_ApiUrl}`);
        }

        if (error.response?.status === 401) {
          console.error("Authentication Error: Invalid or expired token");
          // Clear invalid token
          storage.removeItem(STORAGE_KEYS.API_TOKEN);
          storage.removeItem(STORAGE_KEYS.TENANT_ID);
        }

        throw error;
      }
    );
  }

  private SetHeaders() {
    const token = storage.getItem(STORAGE_KEYS.API_TOKEN);
    const tenantID = storage.getItem(STORAGE_KEYS.TENANT_ID);

    if (token) {
      this.http.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
    if (tenantID) {
      this.http.defaults.headers.common["TenantId"] = tenantID;
    }
    this.http.defaults.headers.common["Accept-Language"] = "es-MX";
  }

  OData(url: string) {
    this.SetHeaders();
    return this.http.get(`odata/${url}`);
  }

  Get(url: string, signal?: AbortSignal) {
    this.SetHeaders();
    return this.http.get(`api/${url}`, { signal });
  }

  Post(url: string, data: any, signal?: AbortSignal) {
    this.SetHeaders();
    return this.http.post(`api/${url}`, data, { signal });
  }

  async Stream(
    url: string,
    options: {
      method?: "GET" | "POST";
      body?: any;
      onData: (data: any) => void;
      onError?: (error: Error) => void;
      onComplete?: () => void;
      signal?: AbortSignal;
    }
  ): Promise<void> {
    const { method = "GET", body, onData, onError, onComplete, signal } = options;

    const token = storage.getItem(STORAGE_KEYS.API_TOKEN);
    const tenantID = storage.getItem(STORAGE_KEYS.TENANT_ID);

    const fetchHeaders = new Headers();

    if (token) {
      fetchHeaders.append("Authorization", `Bearer ${token}`);
    }

    if (tenantID) {
      fetchHeaders.append("TenantId", tenantID);
    }

    if (body) {
      fetchHeaders.append("Content-Type", "application/json");
    }

    fetchHeaders.append("Accept-Language", "es-MX");
    fetchHeaders.append("Module", "Common");
    fetchHeaders.append("Accept", "application/x-ndjson");

    try {
      const response = await fetch(`${process.env.REACT_APP_ApiUrl}api/${url}`, {
        method,
        headers: fetchHeaders,
        body: body ? JSON.stringify(body) : undefined,
        signal,
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error("Authentication Error: Invalid or expired token");
          storage.removeItem(STORAGE_KEYS.API_TOKEN);
          storage.removeItem(STORAGE_KEYS.TENANT_ID);
          const error = new Error("Authentication Error: Invalid or expired token");
          Sentry.captureException(error);
          onError?.(error);
          return;
        }
        const error = new Error(`HTTP Error: ${response.status} ${response.statusText}`);
        Sentry.captureException(error);
        onError?.(error);
        return;
      }

      if (!response.body) {
        const error = new Error("No readable stream available");
        Sentry.captureException(error);
        onError?.(error);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer.trim()) {
            try {
              const jsonChunk = JSON.parse(buffer);
              onData(jsonChunk);
            } catch (err) {
              console.error("Error parsing final chunk:", err);
              console.log("Fragmento no parseado:", buffer);
            }
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        lines.forEach((line) => {
          if (line.trim()) {
            try {
              const jsonChunk = JSON.parse(line);
              onData(jsonChunk);
            } catch (err) {
              console.error("Error parsing chunk:", err);
              console.log("Fragmento no parseado:", line);
            }
          }
        });
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        console.log("Stream aborted");
        onError?.(new Error("Stream aborted"));
        return;
      }
      Sentry.captureException(error);
      console.error("Streaming Error:", error);
      onError?.(error instanceof Error ? error : new Error("Unknown streaming error"));
    } finally {
      onComplete?.();
    }
  }
}

export const api = new Api();
